import { DataTable } from "../../../../components/DataTable";
import { categoriesGroups, scopeThreeCategories } from "../helpers";
import { sortAlphabeticallyByAttribute } from "../../../../utils";
import {
  generatedWaste
} from "../../../OrganizationQuestionnaire/components/Categories/components/Category/helpers";
import { ScopeThreeCategoryThreeFactors } from "./index";
import { useTranslation } from "react-i18next";
import { Text } from "@chakra-ui/react";
import * as React from "react";
import { useCallback } from "react";

function ScopeThreeFactorsGrid({ data, selectedCategory }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "conversionFactors.headers"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const getColumns = useCallback((category) => {
    switch (category.group) {
      case categoriesGroups.CATEGORY_ONE_AND_TWO: {
        return [
          { header: t("productCategory"), accessorKey: "productCategory", size: 300 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false }
        ];
      }
      case categoriesGroups.CATEGORY_FOUR_AND_NINE: {
        return [
          { header: t("typeOfTransportation"), accessorKey: "type", size: 200 },
          { header: t("subtypeOfTransportation"), accessorKey: "subtype", size: 220 },
          { header: t("subtypeTwoOfTransportation"), accessorKey: "subtype2", size: 220 },
          { header: t("unit"), accessorKey: "unit", size: 150 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 300, enableColumnFilter: false }
        ];
      }
      case categoriesGroups.CATEGORY_FIVE_AND_TWELVE: {
        return [
          { header: t("wasteType"), accessorKey: "wasteType", size: 200 },
          { header: t("disposalMethod"), accessorKey: "disposalMethod", size: 200 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false }
        ];
      }
      case categoriesGroups.CATEGORY_SIX_AND_SEVEN: {
        return [
          { header: t("modeOfTransport"), accessorKey: "modeOfTransport", size: 150 },
          { header: t("transportSubtype"), accessorKey: "transportSubtype", size: 200 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false }
        ];
      }
      case categoriesGroups.CATEGORY_ELEVEN: {
        return [
          { header: t("type"), accessorKey: "type", size: 200 },
          { header: t("conversionFactor"), accessorKey: "conversionFactor", size: 200, enableColumnFilter: false }
        ];
      }
      default:
        return [];
    }
  }, [t]);

  const getMappedData = useCallback((category) => {
    switch (category.group) {
      case categoriesGroups.CATEGORY_ONE_AND_TWO: {
        return data[category.group].map(el => {
          return {
            productCategory: `${el.productCategory.code} - ${el.productCategory.name}`,
            conversionFactor: el.conversionFactor
          };
        });
      }
      case categoriesGroups.CATEGORY_FOUR_AND_NINE: {
        return data[category.group]
          .map(el => {
            return {
              type: el.typeOfTransport.name,
              subtype: el.subtypeOfTransport.name,
              subtype2: el.subtypeTwo.name,
              unit: el.unit.name,
              conversionFactor: el.conversionFactor
            };
          })
          .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "type"));
      }
      case categoriesGroups.CATEGORY_FIVE_AND_TWELVE: {
        const findWasteType = (type) => {
          const found = generatedWaste.find(el => Object.keys(el.types).includes(type));
          if (typeof found !== "undefined") {
            return globalTranslation(`organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.wasteType.options.${found.activity}.${type}`, { defaultValue: type });
          }
          return type;
        };

        return data[category.group]
          .map(el => {
            return {
              wasteType: findWasteType(el.wasteType),
              disposalMethod: globalTranslation(`organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.disposalMethod.options.${el.disposalMethod}`, { defaultValue: el.disposalMethod }),
              conversionFactor: el.conversionFactor
            };
          })
          .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "wasteType"));
      }
      case categoriesGroups.CATEGORY_SIX_AND_SEVEN: {
        return data[category.group]
          .map(el => {
            return {
              modeOfTransport: globalTranslation(`organizationQuestionnaire.scope3.inputs.modeOfTransportOptions.${el.modeOfTransport}`, { defaultValue: el.modeOfTransport }),
              transportSubtype: el.transportSubtype.toLowerCase() === "walking" ||
              el.transportSubtype.toLowerCase() === "cycling" ? "" :
                globalTranslation(`organizationQuestionnaire.scope3.inputs.transportSubtypeOptions.${el.modeOfTransport}.${el.transportSubtype}`, { defaultValue: el.transportSubtype }),
              conversionFactor: el.conversionFactor
            };
          })
          .sort((a, b) => sortAlphabeticallyByAttribute(a, b, "modeOfTransport"));
      }
      case categoriesGroups.CATEGORY_ELEVEN: {
        if (!data[category.group]["soldElectricityConversionFactor"] && !data[category.group]["soldGasConversionFactor"]) {
          return [];
        } else {
          return [
            { type: t("soldElectricity"), conversionFactor: data[category.group]["soldElectricityConversionFactor"] },
            { type: t("soldGas"), conversionFactor: data[category.group]["soldGasConversionFactor"] }
          ];
        }
      }
      default:
        return [];
    }
  }, [data, globalTranslation, t]);

  const renderContent = () => {
    const category = scopeThreeCategories.find(el => el.value === selectedCategory);
    if (typeof category !== "undefined") {
      if (category.group === categoriesGroups.CATEGORY_THREE) {
        return <ScopeThreeCategoryThreeFactors data={data} />;
      } else {
        return !getMappedData(category) || getMappedData(category).length === 0 ?
          <Text fontSize="lg" fontWeight="bold">
            {globalTranslation("noDataAvailable")}
          </Text> :
          <DataTable
            key={category.value}
            data={getMappedData(category)}
            columns={getColumns(category)}
            enableFilters={true}
          />;
      }
    }
    return null;
  };

  return renderContent();
}

export default ScopeThreeFactorsGrid;